import { DateTime } from 'luxon';
import { match } from 'ts-pattern';

/**
 * Dynamic, viewer-relative date placeholders for date filters — the date counterpart to the
 * "Me"/"Mine" sentinels. A token is stored as the filter value and only expanded to a concrete
 * `[start, end]` range at where-build time (via the date field's `transform`), so a shared saved
 * view keeps meaning "last 30 days from *today*" rather than freezing a date when it was saved.
 */

export const RELATIVE_DATE_TOKENS = [
    'today',
    'last-7-days',
    'last-30-days',
    'last-90-days',
    'this-month',
    'this-year',
] as const;

export type RelativeDateToken = (typeof RELATIVE_DATE_TOKENS)[number];

/** Picker options for the "Relative" group, in display order. Sentence-case British labels. */
export const RELATIVE_DATE_PRESETS: { value: RelativeDateToken; label: string }[] = [
    { value: 'today', label: 'Today' },
    { value: 'last-7-days', label: 'Last 7 days' },
    { value: 'last-30-days', label: 'Last 30 days' },
    { value: 'last-90-days', label: 'Last 90 days' },
    { value: 'this-month', label: 'This month' },
    { value: 'this-year', label: 'This year' },
];

const PRESET_LABELS = Object.fromEntries(RELATIVE_DATE_PRESETS.map((p) => [p.value, p.label])) as Record<
    RelativeDateToken,
    string
>;

/** Narrow a stored value to a *known* relative token. Any other string is a custom/ISO date. */
export function isRelativeDateToken(value: unknown): value is RelativeDateToken {
    return typeof value === 'string' && (RELATIVE_DATE_TOKENS as readonly string[]).includes(value);
}

export function relativeDateLabel(token: string): string | undefined {
    return isRelativeDateToken(token) ? PRESET_LABELS[token] : undefined;
}

/**
 * Expand a relative token to a concrete `[start, end]` range against the current moment.
 *
 * "Last N days" means N calendar days **including today** (Last 7 days = today + the 6 prior days),
 * anchored to local day boundaries to match `buildDateWhere`'s local-day convention. Returns `null`
 * for unknown tokens so the caller skips the filter.
 */
export function resolveRelativeDateRange(token: string): [Date, Date] | null {
    const now = DateTime.now();
    const range = (start: DateTime, end: DateTime): [Date, Date] => [start.toJSDate(), end.toJSDate()];
    const lastNDays = (n: number) => range(now.minus({ days: n - 1 }).startOf('day'), now.endOf('day'));

    return match(token)
        .with('today', () => range(now.startOf('day'), now.endOf('day')))
        .with('last-7-days', () => lastNDays(7))
        .with('last-30-days', () => lastNDays(30))
        .with('last-90-days', () => lastNDays(90))
        .with('this-month', () => range(now.startOf('month'), now.endOf('month')))
        .with('this-year', () => range(now.startOf('year'), now.endOf('year')))
        .otherwise(() => null);
}
